<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[10060] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      @keydown.escape.window="onEscape"
    >
      <div
        class="cliente-submodal cliente-recebivel-modal shadow-2xl w-full max-w-lg border flex flex-col"
        @click.stop
      >
        <header class="cliente-submodal__header cliente-recebivel-modal__header">
          <div class="cliente-recebivel-modal__icon-wrap shrink-0">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="cliente-recebivel-modal__title">
              {{ isEdit ? t('workspaceClienteModal.financeiro.edit') : t('workspaceClienteModal.financeiro.add') }}
            </h3>
            <p class="cliente-recebivel-modal__subtitle">{{ t('workspaceClienteModal.financeiro.formSubtitle') }}</p>
          </div>
          <button
            type="button"
            class="cliente-recebivel-modal__close"
            :aria-label="t('common.close')"
            @click="$emit('close')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </header>

        <div class="cliente-submodal__body space-y-4">
          <div class="cliente-recebivel-modal__valor-block">
            <label class="cliente-recebivel-modal__field-label">
              {{ t('workspaceClienteModal.financeiro.valor') }}
              <span class="cliente-recebivel-modal__req">*</span>
            </label>
            <div
              class="cliente-recebivel-modal__valor-input"
              :class="{ 'cliente-recebivel-modal__valor-input--error': fieldErrors.valor }"
            >
              <img
                :src="moneyIcon"
                alt=""
                class="cliente-recebivel-modal__money-icon"
                aria-hidden="true"
              />
              <input
                :value="valorDisplay"
                type="text"
                inputmode="decimal"
                class="cliente-recebivel-modal__valor-field"
                :placeholder="t('workspaceClienteModal.financeiro.valorPlaceholder')"
                @input="onValorInput"
              />
            </div>
            <p v-if="fieldErrors.valor" class="cliente-recebivel-modal__error">{{ fieldErrors.valor }}</p>
          </div>

          <ClienteFormField
            v-model="local.descricao"
            :label="t('workspaceClienteModal.financeiro.descricao')"
            multiline
            :rows="2"
            :placeholder="t('workspaceClienteModal.financeiro.descricaoPlaceholder')"
          >
            <template #icon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
              </svg>
            </template>
          </ClienteFormField>

          <ClienteFormField
            v-model="local.dataVencimento"
            :label="t('workspaceClienteModal.financeiro.vencimento')"
            type="date"
            required
            :error="fieldErrors.dataVencimento"
            @update:model-value="fieldErrors.dataVencimento = ''"
          >
            <template #icon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </template>
          </ClienteFormField>

          <div v-if="!isEdit" class="cliente-recebivel-modal__rec-block">
            <button
              type="button"
              class="cliente-recebivel-modal__rec-card"
              :class="{ 'cliente-recebivel-modal__rec-card--active': local.mensal }"
              @click="local.mensal = !local.mensal"
            >
              <span class="cliente-recebivel-modal__rec-card__icon" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
              </span>
              <span class="cliente-recebivel-modal__rec-card__text">
                <strong>{{ t('workspaceClienteModal.financeiro.mensalToggle') }}</strong>
                <small>{{ t('workspaceClienteModal.financeiro.mensalSub') }}</small>
              </span>
              <span class="cliente-recebivel-modal__rec-card__switch" :aria-pressed="local.mensal" />
            </button>

            <div v-if="local.mensal" class="cliente-recebivel-modal__rec-body">
              <span class="cliente-recebivel-modal__rec-body-label">{{ t('workspaceClienteModal.financeiro.mensalModoLabel') }}</span>
              <div class="cliente-recebivel-modal__rec-modes" role="radiogroup" :aria-label="t('workspaceClienteModal.financeiro.mensalModoLabel')">
                <button
                  type="button"
                  class="cliente-recebivel-modal__rec-mode-btn"
                  :class="{ 'cliente-recebivel-modal__rec-mode-btn--active': recMode === 'vezes' }"
                  @click="recMode = 'vezes'"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  {{ t('workspaceClienteModal.financeiro.mensalVezes') }}
                </button>
                <button
                  type="button"
                  class="cliente-recebivel-modal__rec-mode-btn"
                  :class="{ 'cliente-recebivel-modal__rec-mode-btn--active': recMode === 'sempre' }"
                  @click="recMode = 'sempre'"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                  {{ t('workspaceClienteModal.financeiro.mensalSempre') }}
                </button>
              </div>

              <div v-if="recMode === 'vezes'" class="cliente-recebivel-modal__rec-qty">
                <label class="cliente-recebivel-modal__field-label" for="rec-qty-input">{{ t('workspaceClienteModal.financeiro.mensalQuantidade') }}</label>
                <div class="cliente-recebivel-modal__qty-wrap">
                  <button type="button" class="cliente-recebivel-modal__qty-step" :disabled="local.repeticoes <= 2" @click="bumpRepeticoes(-1)">−</button>
                  <input id="rec-qty-input" v-model.number="local.repeticoes" type="number" min="2" max="120" class="cliente-recebivel-modal__qty-input" />
                  <button type="button" class="cliente-recebivel-modal__qty-step" :disabled="local.repeticoes >= 120" @click="bumpRepeticoes(1)">+</button>
                  <span class="cliente-recebivel-modal__qty-suffix">{{ t('workspaceClienteModal.financeiro.mensalMesesSuffix') }}</span>
                </div>
              </div>

              <p v-else class="cliente-recebivel-modal__rec-hint">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                {{ t('workspaceClienteModal.financeiro.mensalSempreHint') }}
              </p>
            </div>
          </div>

          <div v-if="showPreview" class="cliente-recebivel-modal__preview">
            <div class="cliente-recebivel-modal__preview-head">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
              <span>{{ t('workspaceClienteModal.financeiro.previewTitle') }}</span>
            </div>
            <p class="cliente-recebivel-modal__preview-valor">{{ previewValorFormatted }}</p>
            <p v-if="local.descricao?.trim()" class="cliente-recebivel-modal__preview-desc">
              {{ local.descricao.trim() }}
            </p>
            <p class="cliente-recebivel-modal__preview-due">
              {{ t('workspaceClienteModal.financeiro.previewDue', { date: previewDateFormatted }) }}
            </p>
            <ul v-if="previewParcelas.length > 1" class="cliente-recebivel-modal__preview-list">
              <li v-for="p in previewParcelas" :key="p.dataVencimento">{{ p.dataVencimento }} — {{ formatCurrency(p.valor) }}</li>
            </ul>
          </div>
        </div>

        <footer class="cliente-submodal__footer cliente-recebivel-modal__footer">
          <button type="button" class="btn-secondary" @click="$emit('close')">
            {{ t('common.cancel') }}
          </button>
          <button
            type="button"
            class="btn-primary cliente-recebivel-modal__save"
            :disabled="!canSave"
            @click="save"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            {{ t('common.save') }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ClienteFormField from './ClienteFormField.vue'
import { generateRecebiveisMensais } from '@/utils/clienteRecebivelRecorrencia'
import moneyIcon from '@/assets/money-icon.png'
import '@/assets/styles/cliente-modal-theme.css'
import '@/assets/styles/cliente-recebivel-modal.css'

const props = defineProps({
  open: { type: Boolean, default: false },
  initial: { type: Object, default: null },
  locale: { type: String, default: 'pt-BR' }
})

const emit = defineEmits(['close', 'save'])

const { t } = useI18n()

const empty = () => ({ valor: null, dataVencimento: '', descricao: '', mensal: false, repeticoes: 6 })

const local = ref(empty())
const recMode = ref('vezes')
const valorDisplay = ref('')
const fieldErrors = ref({ valor: '', dataVencimento: '' })

const isEdit = computed(() => props.initial != null)

const canSave = computed(() => {
  const valor = Number(local.value.valor || 0)
  return valor > 0 && !!local.value.dataVencimento
})

const showPreview = computed(() => canSave.value)

const previewValorFormatted = computed(() => formatCurrency(local.value.valor))

const previewDateFormatted = computed(() => {
  const raw = local.value.dataVencimento
  if (!raw) return '—'
  const d = new Date(`${raw}T12:00:00`)
  if (Number.isNaN(d.getTime())) return raw
  const loc = props.locale?.startsWith('en') ? 'en-US' : props.locale?.startsWith('es') ? 'es-ES' : 'pt-BR'
  return d.toLocaleDateString(loc, { day: '2-digit', month: 'long', year: 'numeric' })
})

const previewParcelas = computed(() => {
  if (isEdit.value || !local.value.mensal || !local.value.dataVencimento || !local.value.valor) return []
  return generateRecebiveisMensais({
    valor: local.value.valor,
    descricao: local.value.descricao,
    dataInicio: local.value.dataVencimento,
    repeticoes: recMode.value === 'sempre' ? null : local.value.repeticoes,
    sempre: recMode.value === 'sempre'
  })
})

function parseCurrencyMask (raw) {
  const digits = String(raw || '').replace(/\D/g, '')
  if (!digits) return 0
  return Number(digits) / 100
}

function formatCurrency (value) {
  const n = Number(value || 0)
  const loc = props.locale?.startsWith('en') ? 'en-US' : 'pt-BR'
  return n.toLocaleString(loc, { style: 'currency', currency: 'BRL' })
}

function bumpRepeticoes (delta) {
  const next = Math.min(120, Math.max(2, Number(local.value.repeticoes || 2) + delta))
  local.value.repeticoes = next
}

function onValorInput (event) {
  const parsed = parseCurrencyMask(event?.target?.value ?? '')
  local.value.valor = parsed > 0 ? parsed : null
  valorDisplay.value = parsed > 0 ? formatCurrency(parsed).replace(/^R\$\s?/, '') : ''
  fieldErrors.value.valor = ''
}

function validate () {
  fieldErrors.value = { valor: '', dataVencimento: '' }
  const valor = Number(local.value.valor || 0)
  if (valor <= 0) {
    fieldErrors.value.valor = t('workspaceClienteModal.financeiro.validationValor')
    return false
  }
  if (!local.value.dataVencimento) {
    fieldErrors.value.dataVencimento = t('workspaceClienteModal.financeiro.validationVencimento')
    return false
  }
  return true
}

function syncFromInitial () {
  if (props.initial) {
    local.value = {
      valor: props.initial.valor,
      dataVencimento: props.initial.dataVencimento || '',
      descricao: props.initial.descricao || '',
      mensal: false,
      repeticoes: 6
    }
    recMode.value = 'vezes'
    valorDisplay.value = props.initial.valor
      ? formatCurrency(props.initial.valor).replace(/^R\$\s?/, '')
      : ''
  } else {
    local.value = empty()
    valorDisplay.value = ''
  }
  fieldErrors.value = { valor: '', dataVencimento: '' }
}

watch(() => props.open, (isOpen) => {
  if (isOpen) syncFromInitial()
})

watch(() => props.initial, () => {
  if (props.open) syncFromInitial()
})

const onEscape = (event) => {
  event.stopPropagation()
  emit('close')
}

const save = () => {
  if (!validate()) return
  const payload = {
    valor: Number(local.value.valor),
    dataVencimento: local.value.dataVencimento,
    descricao: (local.value.descricao || '').trim(),
    mensal: !isEdit.value && local.value.mensal,
    repeticoes: recMode.value === 'sempre' ? null : Math.max(2, Number(local.value.repeticoes) || 2),
    sempre: recMode.value === 'sempre'
  }
  if (payload.mensal) {
    payload.parcelas = generateRecebiveisMensais({
      valor: payload.valor,
      descricao: payload.descricao,
      dataInicio: payload.dataVencimento,
      repeticoes: payload.repeticoes,
      sempre: payload.sempre
    })
  }
  emit('save', payload)
  emit('close')
}
</script>
